import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { Save as SaveIcon } from '@mui/icons-material';

import {
  FormDateInput,
  FormTextInput,
  GlobalButton,
  LoaderBackdrop,
  NavigateLinks,
} from '@/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';

import { DriverDocuments, EditableDriverProfilePicture } from '../../components';
import {
  childrenDriversPageBreadcrumbs,
  DEFAULT_STATUS_CHIP_CLASS,
  DRIVER_STATUS_CHIP_CLASSES,
} from '../../config';
import { DriverStatus, DriverStatusLabels } from '../../enums';
import {
  defaultEditDriverValues,
  editDriverSchema,
  type EditDriverSchema,
} from '../../models';
import {
  useGetDriverByIdQuery,
  useGetDriverProfilePictureQuery,
} from '../../services';
import { useDriverActions } from '../../hooks';

const EditDriver = () => {
  const { driverId } = useParams<{ driverId: string }>();
  const navigate = useNavigate();
  const { onUpdateDriver, isUpdatingDriver } = useDriverActions();

  const breadcrumbs = useMemo(
    () => childrenDriversPageBreadcrumbs(navigate),
    [navigate]
  );

  const {
    data: driver,
    isLoading,
    isError,
  } = useGetDriverByIdQuery({ id: driverId || '' }, { skip: !driverId });

  const { data: profilePictureData } = useGetDriverProfilePictureQuery(
    { id: driverId || '' },
    { skip: !driverId }
  );
  const profilePictureUrl = profilePictureData?.imageUrl ?? null;

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { isValid, isDirty },
  } = useForm<EditDriverSchema>({
    resolver: zodResolver(editDriverSchema),
    mode: 'onChange',
    shouldFocusError: false,
    defaultValues: defaultEditDriverValues,
  });

  useEffect(() => {
    if (driver?.id) {
      reset({
        firstName: driver.firstName || '',
        lastName: driver.lastName || '',
        email: driver.email || '',
        documentNumber: String(driver.documentNumber ?? '').replace(/\D/g, ''),
        birthDate: driver.birthDate ? new Date(driver.birthDate) : null,
        workPhoneNumber: driver.workPhoneNumber ?? '',
        personalPhoneNumber: driver.personalPhoneNumber ?? '',
        legacyNumber: driver.legacyNumber ?? '',
      });
      void trigger();
    }
  }, [driver, reset, trigger]);

  const onSubmit = (data: EditDriverSchema) => {
    if (!driverId || !driver?.id) return;
    void onUpdateDriver(driverId, data, {
      existingDriver: driver,
      onSuccess: () => navigate(`/business/drivers/${driverId}`),
    });
  };

  if (!driverId) {
    return (
      <section className='flex flex-col gap-6 w-full h-full'>
        <NavigateLinks actualPage='Editar conductor' breadcrumbs={breadcrumbs} />
        <div className='text-center text-red-500 py-8'>
          El ID del conductor es requerido
        </div>
      </section>
    );
  }

  if (isLoading) {
    return <LoaderBackdrop />;
  }

  if (isError || !driver?.id) {
    return (
      <section className='flex flex-col gap-6 w-full h-full'>
        <NavigateLinks actualPage='Editar conductor' breadcrumbs={breadcrumbs} />
        <div className='text-center text-red-500 py-8'>
          Error al cargar el conductor o conductor no encontrado
        </div>
      </section>
    );
  }

  const fullName = `${driver.firstName || ''} ${driver.lastName || ''}`.trim();
  const statusLabel =
    DriverStatusLabels[driver.status as DriverStatus] || driver.status || '';

  return (
    <section className='flex flex-col gap-6 w-full h-full'>
      <NavigateLinks
        actualPage={`Editar: ${fullName || 'Conductor'}`}
        breadcrumbs={breadcrumbs}
      />

      <form
        onSubmit={handleSubmit(onSubmit)}
        className='flex flex-col gap-6 w-full'
      >
        <div className='flex flex-wrap justify-end gap-3'>
          <GlobalButton
            variant={GlobalButtonVariant.secondaryButton}
            action={() => navigate(`/business/drivers/${driverId}`)}
          >
            Cancelar
          </GlobalButton>
          <GlobalButton
            variant={GlobalButtonVariant.primaryButton}
            action={handleSubmit(onSubmit)}
            disabled={!isValid || !isDirty || isUpdatingDriver}
          >
            <SaveIcon sx={{ fontSize: 20, mr: 0.5 }} />
            {isUpdatingDriver ? 'Guardando...' : 'Guardar cambios'}
          </GlobalButton>
        </div>

        <div className='flex flex-col gap-6 w-full min-w-0'>
          <div className='bg-white rounded-xl shadow-sm p-6'>
            <div className='flex flex-wrap items-center justify-between gap-4 mb-6'>
              <h2 className='text-xl font-bold text-gray-800'>Información personal</h2>
              <span
                className={`px-3 py-1 rounded-md text-sm font-semibold ${
                  DRIVER_STATUS_CHIP_CLASSES[driver.status] ?? DEFAULT_STATUS_CHIP_CLASS
                }`}
              >
                {statusLabel}
              </span>
            </div>
            <div className='flex flex-col sm:flex-row gap-6 mb-6'>
              <EditableDriverProfilePicture
                  driverId={driverId}
                  fullName={fullName}
                  profilePictureUrl={profilePictureUrl}
                />
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1 min-w-0'>
                <FormTextInput
                  name='firstName'
                  placeholder='Nombre'
                  control={control}
                  required
                />
                <FormTextInput
                  name='lastName'
                  placeholder='Apellido'
                  control={control}
                  required
                />
                <FormTextInput
                  name='email'
                  placeholder='Correo electrónico'
                  control={control}
                  required
                />
                <FormTextInput
                  name='documentNumber'
                  placeholder='Número de documento'
                  control={control}
                  type='number'
                  required
                />
                <FormDateInput
                  name='birthDate'
                  placeholder='Fecha de nacimiento'
                  control={control}
                />
                <FormTextInput
                  name='workPhoneNumber'
                  placeholder='Teléfono laboral'
                  control={control}
                />
                <FormTextInput
                  name='personalPhoneNumber'
                  placeholder='Teléfono personal'
                  control={control}
                />
                <FormTextInput
                  name='legacyNumber'
                  placeholder='Número de celular del sistema viejo'
                  control={control}
                />
              </div>
            </div>
          </div>

          <div className='bg-white rounded-xl shadow-sm p-6'>
            <DriverDocuments driverId={driverId} editable />
          </div>
        </div>
      </form>
    </section>
  );
};

export default EditDriver;
